import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp } from "@inertiajs/react"
import { StrictMode, createElement } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import i18n, { applyLocaleFromPageProps, ensureLocaleLoaded } from "@/i18n"
import { initializeTheme } from "@/hooks/use-appearance"
import { installRybbitPageviewGuard } from "@/lib/rybbit-pageviews"
import PersistentLayout from "@/layouts/persistent-layout"

// Runs before the deferred Rybbit snippet. The layout tag must stay after
// this entrypoint so the guard is in place when Rybbit hooks history.
installRybbitPageviewGuard()

const appName = import.meta.env.VITE_APP_NAME ?? "Calm Companies"

function readInitialLocale(): string {
  const pageScript = document.querySelector<HTMLScriptElement>(
    'script[data-page="app"]',
  )
  if (!pageScript?.textContent) return "en"

  try {
    const page = JSON.parse(pageScript.textContent) as {
      props?: { locale?: string }
    }
    return page.props?.locale ?? "en"
  } catch {
    return "en"
  }
}

async function bootInertiaApp() {
  // Load the active locale before hydrate/render so non-EN SSR HTML
  // does not flash English keys while locale JSON chunks download.
  const initialLocale = readInitialLocale()
  await ensureLocaleLoaded(initialLocale)
  if (i18n.language !== initialLocale) {
    await i18n.changeLanguage(initialLocale)
  }

  await createInertiaApp({
    // Set default page title
    // see https://inertia-rails.dev/guide/title-and-meta
    //
    title: (title) => (title ? `${appName} | ${title}` : appName),

    resolve: (name) => {
      const pages = import.meta.glob<{ default: ResolvedComponent }>(
        "../pages/**/*.tsx",
      )
      const loadPage = pages[`../pages/${name}.tsx`]
      if (!loadPage) {
        console.error(`Missing Inertia page component: '${name}.tsx'`)
        throw new Error(`Missing Inertia page component: '${name}.tsx'`)
      }

      return loadPage().then((page) => {
        // To use a default layout, import the Layout component
        // and use the following line.
        // see https://inertia-rails.dev/guide/pages#default-layouts
        page.default.layout ??= [PersistentLayout]
        return page.default
      })
    },

    setup({ el, App, props }) {
      void applyLocaleFromPageProps(props.initialPage.props)

      if (el.hasChildNodes()) {
        hydrateRoot(el, createElement(App, props))
        return
      }
      createRoot(el).render(
        <StrictMode>
          <App {...props} />
        </StrictMode>,
      )
    },

    defaults: {
      form: {
        forceIndicesArrayFormatInFormData: false,
      },
    },

    progress: {
      color: "#4B5563",
    },
  })
}

void bootInertiaApp().catch((error: unknown) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
